/**
 * A tiny global "something is in flight" counter, so the branded spinner can be
 * shown over the whole screen while the app is actually waiting — a form save, a
 * photo upload, any mutation that takes a moment. GETs (background polls and
 * refetches) are deliberately NOT counted: a spinner that flashed every 20s poll
 * would be noise, not reassurance.
 *
 * No imports, so api.ts can call this without any circular dependency.
 */
let count = 0;
const subs = new Set<(active: boolean) => void>();

function emit(): void {
  const active = count > 0;
  for (const fn of subs) fn(active);
}

export function beginBusy(): void {
  count += 1;
  emit();
}

export function endBusy(): void {
  count = Math.max(0, count - 1);
  emit();
}

/** Run an async operation flagged as busy, always clearing the flag. */
export async function withBusy<T>(op: () => Promise<T>): Promise<T> {
  beginBusy();
  try {
    return await op();
  } finally {
    endBusy();
  }
}

export function subscribeBusy(fn: (active: boolean) => void): () => void {
  subs.add(fn);
  fn(count > 0);
  return () => { subs.delete(fn); };
}
